'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import { en } from '@/lib/translations/en';
import { es } from '@/lib/translations/es';

import { fr } from '@/lib/translations/fr';
import { de } from '@/lib/translations/de';

type Language = 'en' | 'es' | 'fr' | 'de';
type Translations = typeof en;

interface LanguageContextType {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: Translations;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
    const [language, setLanguage] = useState<Language>('en');

    useEffect(() => {
        const savedLang = localStorage.getItem('language') as Language;
        if (savedLang && ['en', 'es', 'fr', 'de'].includes(savedLang)) {
            setLanguage(savedLang);
        }
    }, []);

    const handleSetLanguage = (lang: Language) => {
        setLanguage(lang);
        localStorage.setItem('language', lang);
    };

    const getT = (lang: Language) => {
        switch (lang) {
            case 'es': return es;
            case 'fr': return fr;
            case 'de': return de;
            default: return en;
        }
    };

    const t = getT(language);

    return (
        <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useTranslation() {
    const context = useContext(LanguageContext);
    if (context === undefined) {
        throw new Error('useTranslation must be used within a LanguageProvider');
    }
    return context;
}
